import React, { useCallback, useEffect, useState } from 'react'
import { cookiesKeyNames } from '@revbox/common-fe'
import { getPubAbbr, getDaysDiff, getCachedEurUsdRateSync } from '@revbox/helpers-fe'
import { Link, useNavigate } from 'react-router-dom'
import { Card, Col, Row, Container, Placeholder } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { addPubParamToUrl } from '@helpers/add-pub-param-to-url'

// Contexts
import { useCookiesContext } from '../../contexts/CookiesContext'

// Services
import { editCampaign } from '../../services/adv'
import {
    getInventoryCampaignByInventoryAndCampaign,
    getPeriodClusterPPCAuctions,
    getPeriodEmailsPPCAuctions,
    getPeriodBrandingPPCAuctions,
    getPeriodLivePPCAuctions,
    verifyCustomerToken
} from '../../services/micro'
import { getPublisher } from '../../services/pub'

// Translations
import { advAuctionsBreadcrumbs as breadcrumbs, campaignStatusLabels, toasts, helpModalLabels } from '@revbox/localization'
// Mixpanel
import { mixpanelInstance } from '../../mixpanel/instance'
import { modules } from '../../modules'
import { blankPageMap } from '../../modules/maps'

// Components
import { BrandingPPCAuctions } from './components/auctions/branding/BrandingPPCAuctions'
import { EmailsPPCAuctions } from './components/auctions/emails/EmailsPPCAuctions'
import { SpecificPPCAuctions } from './components/auctions/specific/SpecificPPCAuctions'
import AdvertiserPPCAlerts from './components/auctions/alerts/AdvertiserPPCAlerts'
import CampaignBudget from './components/auctions/modals/CampaignBudget'
import PPCManagmentModal from './components/auctions/modals/PPCManagmentModal/PPCManagmentModal'
import InactiveCampaignWarning from './components/auctions/warnings/InactiveCampaignWarning'
import HelpModal from './components/common/ModalHelp'

// Assets
import pageSvg from '../../assets/svg/server_down.svg'
import TablesNavigation from './components/common/TablesNavigation'
import TopCards from './components/common/TopCards'
import ApvForecastingBanner from './components/common/ApvForecastingBanner'
import { fetchCampaignData, getAnyPPCAuctions } from './components/common/helpers'
import { ClusterPPCAuctions } from './components/auctions/cluster/ClusterPPCAuctions/ClusterPPCAuctions'
import { WithState } from '../../hooks/withState'
// import { ContentPPCAuctions } from './components/auctions/content/ContentPPCAuctions'
// import getPeriodContentPPCAuctions from '@services/micro/get_content_ppc_auctions'
    
const HTR_STAGE_PUBLISHER_ID = 10
const HTR_PUBLISHER_ID = 67
const DEFAULT_ID = 1
const APV_PUBLISHER_ID = 73
const NNX_PUBLISHER_ID = 34

const navigateTo404 = navigate => {
    const publisherIdByAbbr = getPubAbbr()
    navigate('/pages/error-404?pub=' + publisherIdByAbbr)
}

const tabs = {
    cluster: 'cluster',
    specific: 'specific',
    // content: 'content',
    branding: 'branding',
    emails: 'emails',
}

export default function AdvertiserPpc({ isIframe }) {
    const urlParams = new URLSearchParams(window.location.search)
    const campaignId = urlParams.get('campaignId')
    const token = urlParams.get('token')

    const isIframed = window !== window.parent

    const [inventoryCampaign, setInventoryCampaign] = useState(null)
    const [campaign, setCampaign] = useState(null)
    const [newBudget, setNewBudget] = useState(0)
    const [updatePage, setUpdatePage] = useState(0)
    const [publisherId, setPublisherId] = useState(null)
    const [publisher, setPublisher] = useState(null)
    const [closedAlert, setClosedAlert] = useState(false)
    const [errorStatus, setErrorStatus] = useState(null)
    const [isClusterInventoryCampaign, setIsClusterInventoryCampaign] = useState(false)
    const [allChildrenCount, setAllChildrenCount] = useState(0)
    const [anyActiveChild, setAnyChildrenActive] = useState(false)
    const [anyInactiveParent, setAnyInactiveParent] = useState(false)
    const [allowed, setAllowed] = useState(false)
    const [clusterLevel, setClusterLevel] = useState(0)
    const [isTokenVerified, setIsTokenVerified] = useState(null)
    const [budgetLoading, setBudgetLoading] = useState(false)
    const [anyPPCAuctions, setAnyPPCAuctions] = useState(false)
    const [isContentAd, setIsContentAd] = useState(false)
    const [anyBrandingAuctions, setAnyBrandingAuctions] = useState(false)
    const [anyEmailsAuctions, setAnyEmailsAuctions] = useState(false)
    const [numberOfClusterAuctions, setNumberOfClusterAuctions] = useState(0)
    const [ppcActiveAuctions, setPpcActiveAuctions] = useState()
    const [brandingActiveAuctions, setBrandingActiveAuctions] = useState()
    // const [contentActiveAuctions, setContentActiveAuctions] = useState()
    // const [anyContentAuctions, setAnyContentAuctions] = useState(false)
    const [clusterActiveAuctions, setClusterActiveAuctions] = useState()
    const [emailsActiveAuctions, setEmailsActiveAuctions] = useState()
    const [currentTab, setCurrentTab] = useState(null)
    const [anyActiveSpecificBid, setAnyActiveSpecificBid] = useState(null)
    const [anyActiveBrandingBid, setAnyActiveBrandingBid] = useState(null)
    const [anyActiveEmailsBid, setAnyActiveEmailsBid] = useState(null)
    // const [anyActiveContentBid, setAnyActiveContentBid] = useState(null)
    // const [isContentAuctionsData, setIsContentAuctionsData] = useState(false)
    const [clusterInheritedAuctionsAndNotShown, setClusterInheritedAuctionsAndNotShown] = useState([])
    const [showBottom, setShowBottom] = useState(false)
    const [views, setViews] = useState(0)

    const { removeCookiesProperty, cookiesData, getPathKey, getSkinMode, getLanguageAbbr } = useCookiesContext()

    const languageAbbr = getLanguageAbbr()
    const multilangBreadcrumbs = breadcrumbs[languageAbbr]
    const multilangCampaignStatus = campaignStatusLabels[languageAbbr]
    const multilangHelpModal = helpModalLabels[languageAbbr]
    const translatedToasts = toasts[languageAbbr]
    const publisherIdByAbbr = getPubAbbr()
    const navigate = useNavigate()
    const rate = getCachedEurUsdRateSync()

    if (token) {
        verifyCustomerToken(token).then(response => {
            if (response && response.success) {
                setIsTokenVerified(true)
                setPublisherId(Number(response?.customer?.publisher_id))
            } else {
                return setIsTokenVerified(false)
            }
        })
    }
    useEffect(() => {
        console.log('error', errorStatus)
    }, [errorStatus])

    const getData = async (fetchAuctions = true) => {
        try {
            let tokenVerified = false
            if (isIframe) {
                const verifyTokenRes = await verifyCustomerToken(token)

                if (verifyTokenRes && verifyTokenRes.success) {
                    setIsTokenVerified(true)
                    tokenVerified = true
                } else {
                    setIsTokenVerified(false)
                    // setErrorStatus(404)
                    return
                }
            }

            console.log('#212 campaignId ', campaignId)
            const campaignData = await fetchCampaignData(isIframe, tokenVerified, campaignId, token)
            if (!campaignData) {
                console.error(translatedToasts.errorFetchingCampaign)
                // if (isIframe) {
                //     setErrorStatus(404)
                // }

                return
            }

            const publisherIdByAbbr = getPubAbbr()

            if (publisherIdByAbbr === NNX_PUBLISHER_ID) {
                setCurrentTab('cluster')
            }

            const { data: publisher } = await getPublisher(publisherIdByAbbr)

            if (!publisher) {
                console.error(translatedToasts.errorFetchingCampaign)
                // if (isIframe) {
                //     setErrorStatus(404)
                // }

                return
            }

            setPublisher(publisher)
            setPublisherId(publisher.id)
            setCampaign({ ...campaignData.data, ...campaignData?.table })
            setNewBudget(campaignData.data.ppc_monthly_budget)

            removeCookiesProperty('clickedDeactivate')
            removeCookiesProperty('clickedActivate')

            if (fetchAuctions) {
                const auctions = await getAnyPPCAuctions(campaignId)
                console.log('auctions anyAuctions length: ', auctions.anyAuctions.length)
                setAnyPPCAuctions(auctions.anyAuctions)
                setPpcActiveAuctions(auctions.liveAuctions)
                console.log('auctions liveAuctions length: ', auctions.liveAuctions.length)
            }
        } catch (error) {
            console.error(translatedToasts.errorFetchingCampaign)
            // if (isIframe) {
            //     setErrorStatus(404)
            // }
        }
    }

    useEffect(() => {
        getData()
    }, [campaignId])

    // Set current tab
    useEffect(() => {
        if (currentTab) {
            return
        }
        if (numberOfClusterAuctions > 0) {
            setCurrentTab(tabs.cluster)
            return
        } else if (!!anyPPCAuctions) {
            setCurrentTab(tabs.specific)
            return
        } else if (!!anyBrandingAuctions) {
            setCurrentTab(tabs.branding)
            return
        } else if (!!anyEmailsAuctions) {
            setCurrentTab(tabs.emails)
            return
        }
        // else if (!!anyContentAuctions) {
        //     setCurrentTab(tabs.content)
        //     return
        // }
    }, [anyPPCAuctions, anyBrandingAuctions, anyEmailsAuctions, numberOfClusterAuctions])

    const handleCampaignActivate = () => {
        window.location.href = '/adv/campaign/activate?campaignId=' + campaignId
    }

    const handleCloseBottom = () => {
        setIsContentAd(false)
        setShowBottom(false)
    }

    const handleShowBottom = () => {

        mixpanelInstance.track(`Open montly budget popup for ${campaign?.name}`, {
            campaignId
        })

        setShowBottom(true)
    }

    const [isPPCModalOpened, setIsPPCModalOpened] = useState(false)

    const handleClose = () => {
        setIsPPCModalOpened(false)
        setIsContentAd(false)
        getData()
    }

    const currentSkin = getSkinMode()

    const handleClusterShow = e => {
        if (e.column.colId !== 'action') {
            return
        }

        const parentClusterInactive = e.value.split('@')[1]
        const childClusterActive = e.value.split('@')[2] // check this
        const allowed = e.value.split('@')[3]
        const noViews = e.value.split('@')[5] === 'true'
        const views = e.value.split('@')[6] ? Number(e.value.split('@')[6]) : 0

        if (noViews) {
            toast.warning(translatedToasts.noViews)
            return
        }

        if (parentClusterInactive && !childClusterActive) {
            return
        }

        if (allowed === 'false') {
            return
        }

        const isParentClusterInactive = parentClusterInactive === 'true' ? true : false
        const isChildClusterActive = childClusterActive === 'true' ? true : false

        const inventory_id = e.data.ID

        getInventoryCampaignByInventoryAndCampaign(inventory_id, campaignId).then(res => {
            if (res && res.success) {
                console.log('res.data.inventory.inventory_type: ', res.data.inventory.inventory_type)
                if (res.data.inventory.inventory_type === 7) {
                    setIsContentAd(true)
                }
                setIsPPCModalOpened(true)
                // stopTour('advInitialTour')
                setViews(views != 0 ? views : res.data.inventory.forecast_views)
                setInventoryCampaign(res.data)
                setIsClusterInventoryCampaign(true)
                setClusterLevel(e.node.level + 1)
                setAllChildrenCount(e.node.allChildrenCount)
                setAnyChildrenActive(isChildClusterActive)
                setAnyInactiveParent(isParentClusterInactive)
                setAllowed(allowed === 'false' ? false : true)

                mixpanelInstance.track(`Open bid management popup for ${campaign?.name}`, {
                    campaignId
                })

                // TODO: check if this is needed
                // getEstimatedPosition(res.data.id, res.data.cpc)
                //     .then(res => {
                //         setEstimatedPosition(res.data)
                //     })
                //     .catch(err => {
                //         console.log('Error fetching estimated position')
                //     })
            } else {
                toast.warning(translatedToasts.cannotOpenBidManagementPopup)
            }
        })
    }

    const handleShow = e => {
        const inventory_id = e.data.ID

        getInventoryCampaignByInventoryAndCampaign(inventory_id, campaignId).then(res => {
            if (res && res.success) {
                console.log('res.data.inventory.inventory_type: ', res.data.inventory.inventory_type)
                if (res.data.inventory.inventory_type === 7) {
                    setIsContentAd(true)
                }
                setIsPPCModalOpened(true)
                setViews(res.data.inventory.forecasted_views)
                setInventoryCampaign(res.data)
                setIsClusterInventoryCampaign(false)

                mixpanelInstance.track(`Open bid management popup for ${campaign?.name}`, {
                    campaignId
                })

                // TODO: check if this is needed
                // getEstimatedPosition(res.data.id, res.data.cpc)
                //     .then(res => {
                //         setEstimatedPosition(res.data)
                //     })
                //     .catch(err => {
                //         console.log('Error fetching estimated position')
                //     })
            } else {
                toast.warning(translatedToasts.cannotOpenBidManagementPopup)
            }
        })
    }

    const onSaveBudget = () => {
        setBudgetLoading(true)

        mixpanelInstance.track(`Try of montly budget change for ${campaign?.name}`, {
            newMonthlyBudget: newBudget,
            campaignId
        })

        const newBudgetValue = document.getElementById('blockBudget').value

        if (newBudgetValue < 250) {
            toast.warn(translatedToasts.monthlyBudgetCantBeLowerThan250)
            setBudgetLoading(false)
            return
        }

        editCampaign(campaignId, {
            ppc_monthly_budget: Number(newBudgetValue)
        })
            .then(res => {
                if (res.success === false) {
                    toast.error(translatedToasts.errorSavingBudget)
                    return
                }

                setShowBottom(false)
                toast.success(translatedToasts.budgetSavedSuccessfully)

                mixpanelInstance.track(`Montly budget successfully changed for ${campaign?.name}`, {
                    newMonthlyBudget: newBudget,
                    campaignId
                })

                getData(false).then(() => {
                    setBudgetLoading(false)
                })
            })
            .catch(err => {
                toast.error(translatedToasts.errorSavingBudget)
                setBudgetLoading(false)
            })
    }

    const onChangeMonthlyBudget = e => {
        setTimeout(() => {
            if (e.target.value < publisher?.min_budget || isNaN(e.target.value)) {
                toast.warn(translatedToasts.monthlyBudgetCantBeLowerThan(publisher?.min_budget, rate))
                const blockBudget = document.getElementById('blockBudget')

                if (blockBudget) {
                    blockBudget.value = publisher?.min_budget
                }

                setNewBudget(publisher?.min_budget)
                return
            }

            const blockBudget = document.getElementById('blockBudget')

            if (blockBudget) {
                blockBudget.value = e.target.value
            }

            setNewBudget(e.target.value)
            return
        }, 2000)
    }

    const day2 = new Date().setMonth(new Date().getMonth() + 1)
    const days_diff = getDaysDiff(campaign?.ppc_invoice_day, new Date(), new Date(day2).setDate(campaign?.ppc_invoice_day))

    if (isIframe && isTokenVerified === false) {
        return modules(10, blankPageMap)
    }

    if (isIframe && !campaignId) {
        return navigateTo404(navigate)
    }

    const campaignCategories = campaign?.related_categories_ids?.map(category => {
        const label = campaign?.columns?.find(c => c.name === 'related_categories_ids')?.options?.find(o => o.value === category)?.label
        return label
    })

    if ((isIframe && isTokenVerified === true) || !isIframe) {
        if (errorStatus === 404 && isIframe) {
            if ([HTR_PUBLISHER_ID, HTR_STAGE_PUBLISHER_ID].includes(publisherIdByAbbr)) {
                navigateTo404(navigate)
            }
            return (
                <div className="content">
                    <Container>
                        <Row className="gx-5">
                            <Col lg="5" className="d-flex flex-column align-items-center">
                                <h1 className="error-number">404</h1>
                                <h2 className="error-title">Page Not Found</h2>
                                <p className="error-text">
                                    The page you were looking for doesn't exist. You may have mistyped the address or the page may have moved.
                                </p>
                                {isIframed && (
                                    <Link to="/dashboard/sales" className="btn btn-primary btn-error">
                                        Back to Dashboard
                                    </Link>
                                )}
                            </Col>
                            <Col xs="8" lg="6" className="mb-5 mb-lg-0">
                                <object type="image/svg+xml" data={pageSvg} className="w-100" aria-label="svg image"></object>
                            </Col>
                        </Row>
                    </Container>
                </div>
            )
        }

        let isCreditBalanceSmallerThanTopBid =
            campaign && Object.keys(campaign).length && campaign?.ppc_credits_monthly_spend < inventoryCampaign?.top_bid && campaign?.ppc_active

        let isCampaignInactive = campaign && Object.keys(campaign).length && !campaign?.ppc_active

        let isCampaignPaused = campaign && Object.keys(campaign).length && campaign?.ppc_paused

        // Flags for publisher
        const isHTR = [HTR_PUBLISHER_ID, HTR_STAGE_PUBLISHER_ID].includes(publisherIdByAbbr)
        const isNNX = [NNX_PUBLISHER_ID, APV_PUBLISHER_ID].includes(+publisherIdByAbbr)
        const isDefault = publisherId === DEFAULT_ID

        // Flags for alerts
        let isNoBidsFlag =
            ppcActiveAuctions?.length === 0 &&
            clusterActiveAuctions?.length === 0 &&
            brandingActiveAuctions?.length === 0 &&
            emailsActiveAuctions?.length === 0// &&
            // contentActiveAuctions?.length === 0

        let isActivePPCAuctions = ppcActiveAuctions?.length !== 0 && ppcActiveAuctions !== undefined
        // let isActiveClusterAuctions = clusterActiveAuctions?.length !== 0 && clusterActiveAuctions !== undefined
        let isActiveBrandingAuctions = brandingActiveAuctions?.length !== 0 && brandingActiveAuctions !== undefined
        // let isActiveContentAuctions = contentActiveAuctions?.length !== 0 && contentActiveAuctions !== undefined
        // let isActiveEmailsAuctions = emailsActiveAuctions?.length !== 0 && emailsActiveAuctions !== undefined

        // Cannot have multiple alerts at the same time
        if (isCampaignInactive) {
            isNoBidsFlag = false
            isCreditBalanceSmallerThanTopBid = false
            isCampaignPaused = false
        } else if (isNoBidsFlag) {
            isCreditBalanceSmallerThanTopBid = false
            isCampaignInactive = false
            isCampaignPaused = false
        } else if (isCreditBalanceSmallerThanTopBid) {
            isNoBidsFlag = false
            isCampaignInactive = false
            isCampaignPaused = false
        } else if (isCampaignPaused) {
            isNoBidsFlag = false
            isCreditBalanceSmallerThanTopBid = false
            isCampaignInactive = false
        }

        const currentUrlParams = new URLSearchParams(window.location.search)
        const pubValue = currentUrlParams.get('pub')
        const advIdByCookies = cookiesData[getPathKey()]?.[cookiesKeyNames.advertiserId]

        return (
            <React.Fragment>
                <div className={isIframe ? '' : 'main main-app p-3 p-lg-4'}>
                    {isIframe ? (
                        <></>
                    ) : (
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <div>
                                <ol className="breadcrumb fs-sm mb-1">
                                    <li className="breadcrumb-item">
                                        <Link href="/adv/home" to={addPubParamToUrl('/adv/home', pubValue, advIdByCookies)}>
                                            ADV Home
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item d-flex align-items-center">
                                        {campaign ? (
                                            campaign?.name
                                        ) : (
                                            <React.Fragment>
                                                <Placeholder as={Card.Title} animation="glow" style={{ color: 'var(--bs-body-color)' }}>
                                                    <Placeholder style={{ height: 10, width: 100 }} />
                                                </Placeholder>
                                            </React.Fragment>
                                        )}
                                    </li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        {multilangBreadcrumbs.breadcrumb}
                                    </li>
                                </ol>
                                <h2 className="main-title mb-0">{multilangBreadcrumbs.title}</h2>
                            </div>
                        </div>
                    )}

                    <InactiveCampaignWarning isDefault={isDefault} campaign={campaign} handleCampaignActivate={handleCampaignActivate} />
                    <Row className={`g-3 ${isIframe ? 'htr-iframe-bg' : ''}`}>
                        <TopCards
                            publisherIdByAbbr={publisherId ? publisherId : publisherIdByAbbr}
                            campaign={campaign}
                            campaignCategories={campaignCategories}
                            currentSkin={currentSkin}
                            anyActiveSpecificBid={anyActiveSpecificBid}
                            anyActiveBrandingBid={anyActiveBrandingBid}
                            anyActiveClusterBid={clusterActiveAuctions?.length > 0}
                            anyActiveEmailsBid={anyActiveEmailsBid}
                            // anyActiveContentBid={anyActiveContentBid}
                            budgetLoading={budgetLoading}
                            handleShowBottom={handleShowBottom}
                            days_diff={days_diff}
                            handleCampaignActivate={handleCampaignActivate}
                        />
                        {isNNX && (
                            <Col md="12">
                                <ApvForecastingBanner language={languageAbbr} />
                            </Col>
                        )}
                        
                        <AdvertiserPPCAlerts
                            isCreditBalanceSmallerThanTopBid={isCreditBalanceSmallerThanTopBid}
                            isCampaignInactive={isCampaignInactive}
                            isCampaignPaused={isCampaignPaused}
                            clusterInheritedAuctionsAndNotShown={clusterInheritedAuctionsAndNotShown}
                            isNoBidsFlag={isNoBidsFlag}
                            closedAlert={closedAlert}
                            setClosedAlert={setClosedAlert}
                            isHTR={isHTR}
                        />
                        {!budgetLoading && campaign && campaign?.id && (
                            <>
                                <WithState initialState={false}>
                                    {(isHelpModalOpened, setIsHelpModalOpened) => (
                                        <Col md="12" className="px-4">
                                            <TablesNavigation
                                                isHTR={isHTR}
                                                isAPV={isNNX}
                                                campaign={campaign}
                                                anyClusterAuctions={numberOfClusterAuctions > 0}
                                                currentTab={currentTab}
                                                updatePage={updatePage}
                                                setCurrentTab={setCurrentTab}
                                                numberOfClusterAuctions={numberOfClusterAuctions}
                                                anyPPCAuctions={anyPPCAuctions}
                                                isActivePPCAuctions={isActivePPCAuctions}
                                                isActiveBrandingAuctions={isActiveBrandingAuctions}
                                                anyActiveEmailsBid={anyActiveEmailsBid}
                                                multilangCampaignStatus={multilangCampaignStatus}
                                                multilangHelpModal={multilangHelpModal}
                                                closeHelpModal={() => setIsHelpModalOpened(true)}
                                                // anyActiveContentBid={anyActiveContentBid}
                                                // isContentAuctionsData={isContentAuctionsData}
                                            />
                                            {isHelpModalOpened && <HelpModal setShowHelp={setIsHelpModalOpened} />}
                                        </Col>
                                    )}
                                </WithState>
                                <Col md="12">
                                    {isHTR ? (
                                        <>
                                            <SpecificPPCAuctions
                                                display={currentTab}
                                                updatePage={updatePage}
                                                getData={getPeriodLivePPCAuctions}
                                                campaignId={campaign?.id}
                                                setAnyPPCAuctions={setAnyPPCAuctions}
                                                setAnyActiveSpecificBid={setAnyActiveSpecificBid}
                                                onClick={handleShow}
                                                skin={currentSkin}
                                                isHTR={isHTR}
                                            />

                                            <BrandingPPCAuctions
                                                display={currentTab}
                                                updatePage={updatePage}
                                                getData={getPeriodBrandingPPCAuctions}
                                                setBrandingActiveAuctions={setBrandingActiveAuctions}
                                                setAnyBrandingAuctions={setAnyBrandingAuctions}
                                                campaignId={campaign?.id}
                                                setAnyActiveBrandingBid={setAnyActiveBrandingBid}
                                                onClick={handleShow}
                                                skin={currentSkin}
                                                isHTR={isHTR}
                                            />

                                            {/* <ContentPPCAuctions
                                                display={currentTab}
                                                updatePage={updatePage}
                                                getData={getPeriodContentPPCAuctions}
                                                setContentActiveAuctions={setContentActiveAuctions}
                                                setAnyContentAuctions={setAnyContentAuctions}
                                                campaignId={campaign?.id}
                                                setAnyActiveContentBid={setAnyActiveContentBid}
                                                onClick={handleShow}
                                                skin={currentSkin}
                                                isHTR={isHTR}
                                                setIsContentAuctionsData={setIsContentAuctionsData}
                                            /> */}

                                            <EmailsPPCAuctions
                                                display={currentTab}
                                                updatePage={updatePage}
                                                getData={getPeriodEmailsPPCAuctions}
                                                campaignId={campaign?.id}
                                                setEmailsActiveAuctions={setEmailsActiveAuctions}
                                                setAnyEmailsAuctions={setAnyEmailsAuctions}
                                                setAnyActiveEmailsBid={setAnyActiveEmailsBid}
                                                onClick={handleShow}
                                                skin={currentSkin}
                                                isHTR={isHTR}
                                            />
                                        </>
                                    ) : (
                                        <ClusterPPCAuctions
                                            getData={getPeriodClusterPPCAuctions}
                                            campaign_is_paused={campaign?.ppc_paused}
                                            onClick={handleClusterShow}
                                            setClusterActiveAuctions={setClusterActiveAuctions}
                                            setClusterInheritedAuctionsAndNotShown={setClusterInheritedAuctionsAndNotShown}
                                            setNumberOfClusterAuctions={setNumberOfClusterAuctions}
                                            skin={currentSkin}
                                            updatePage={updatePage}
                                        />
                                    )}
                                </Col>
                            </>
                        )}
                        {isPPCModalOpened && (
                            <PPCManagmentModal
                                show={isPPCModalOpened}
                                token={token}
                                isClusterInventoryCampaign={isClusterInventoryCampaign}
                                handleClose={handleClose}
                                views={views}
                                inventoryId={inventoryCampaign?.inventory_id}
                                campaignId={campaignId}
                                updatePage={updatePage}
                                setBudgetLoading={setBudgetLoading}
                                setUpdatePage={setUpdatePage}
                                clusterLevel={clusterLevel}
                                allChildrenCount={allChildrenCount}
                                anyActiveChild={anyActiveChild}
                                anyInactiveParent={anyInactiveParent}
                                allowed={allowed}
                                auctionType={currentTab}
                                isContentAd={isContentAd}
                            />
                        )}

                        {/* {showBottom && (
                            <CampaignBudget
                                handleCloseBottom={handleCloseBottom}
                                campaign={campaign}
                                publisher={publisher}
                                days_diff={days_diff}
                                onChangeMonthlyBudget={onChangeMonthlyBudget}
                                onSaveBudget={onSaveBudget}
                            />
                        )}*/}
                        { showBottom && (
                            <CampaignBudget
                                show={showBottom}
                                handleCloseBottom={handleCloseBottom}
                                campaign={campaign}
                                publisher={publisher}
                                days_diff={days_diff}
                                onChangeMonthlyBudget={onChangeMonthlyBudget}
                                onSaveBudget={onSaveBudget}
                            />
                        )}
                    </Row>
                </div>
            </React.Fragment>
        )
    }
}
